import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Progress } from "../../../components/ui/progress";
import { useAuth } from "../../../contexts/AuthContext";
import { useConvexProjects, useConvexInvestments } from "../../../services/convexService";
import InvestmentModal from "../../../features/projects/components/InvestmentModal";
import { Id } from "../../../../backend/_generated/dataModel";
import {
  Home,
  LogOut,
  Eye,
  DollarSign,
  TrendingUp,
  Target,
  Users,
  Clock,
  FileText,
  Download,
  Image as ImageIcon,
  ArrowLeft
} from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../../../contexts/LanguageContext";
import CurrencyDisplay from "../../../components/currency/CurrencyDisplay";

const ProjectDetail = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user, logout, userType } = useAuth();
  const { getProjectById, getProjectsForInvestors } = useConvexProjects();
  const { createInvestment } = useConvexInvestments();
  const { t } = useLanguage();

  // We'll try to fetch the project directly and handle the case where it doesn't exist
  // Convex will handle validation of the ID format internally
  const project = getProjectById(projectId as Id<"projects">);

  // Check if project data is still loading
  const isProjectLoading = projectId && !project;

  // Check if project has 'sell' purpose and redirect to ProjectSaleDetail
  if (project && project.projectPurpose === "sell") {
    // We need to find the project sale ID for this project
    // For now, redirect to the project sale detail page
    // In a real implementation, we would find the project sale ID
    navigate(`/project-sales/${projectId}`);
    return null; // Return null while redirecting
  }

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const [showInvestmentModal, setShowInvestmentModal] = useState(false);

  const handleInvest = () => {
    if (!user) {
      toast.error("You need to log in to make an investment");
      navigate("/login");
      return;
    }

    if (userType !== "investor") {
      toast.error("Only registered investors can make investments. Please register as an investor first.");
      return;
    }

    if (user.status !== "approved") {
      toast.error("Your investor account needs to be approved before you can make investments.");
      return;
    }

    setShowInvestmentModal(true);
  };

  const handleGoBack = () => {
    if (userType === "admin" || user?.role === "admin") {
      navigate("/admin");
    } else if (userType === "investor") {
      navigate("/dashboard/investor");
    } else if (userType === "project-holder") {
      navigate("/dashboard/project-holder");
    } else {
      navigate("/projects");
    }
  };

  // Check access permissions
  if (project) {
    // Admins can access any project
    if (user?.role !== "admin") {
      // Project holders can only access their own projects
      if (userType === "project-holder" && user?.id !== project.ownerId) {
        return (
          <div className="min-h-screen flex items-center justify-center">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
              <p className="text-muted-foreground mb-6">You can only view projects that you own. This project belongs to another project holder.</p>
              <Button asChild>
                <Link to="/dashboard/project-holder">Back to My Dashboard</Link>
              </Button>
            </div>
          </div>
        );
      }

      // Investors can only access approved projects
      if (userType === "investor" && project.status !== "approved") {
        return (
          <div className="min-h-screen flex items-center justify-center">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-4">Project Not Available</h2>
              <p className="text-muted-foreground mb-6">This project has not yet been approved for investment. Please check back later or explore other approved investment opportunities.</p>
              <Button asChild>
                <Link to="/projects">Back to Projects</Link>
              </Button>
            </div>
          </div>
        );
      }
    }
  }

  // Show loading state while project is being fetched
  if (isProjectLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Loading Project Details</h2>
          <p className="text-muted-foreground mb-6">Please wait while we load the project information. This may take a few seconds.</p>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Project Not Found</h2>
          <p className="text-muted-foreground mb-6">The project you're looking for doesn't exist, has been removed, or you may not have permission to view it.</p>
          <div className="space-y-4">
            <Button asChild>
              <Link to="/projects">Back to Projects</Link>
            </Button>
            <p className="text-sm text-muted-foreground">If you believe this is an error, please contact support.</p>
          </div>
        </div>
      </div>
    );
  }

  // Calculate funding percentage
  const fundingPercentage = project.fundingGoal > 0
    ? Math.round((project.currentFunding / project.fundingGoal) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <img src="/logo.png" alt="Knowvest.plus Logo" className="w-6 h-6 object-contain" />
              </div>
              <h1 className="font-bold text-xl">Knowvest.plus</h1>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleGoBack}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t("common.backToHome")}
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link to="/Home">
                  <Home className="w-4 h-4 mr-2" />
                  Home
                </Link>
              </Button>

              {user ? (
                <Button variant="outline" size="sm" onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              ) : (
                <Button variant="outline" size="sm" asChild>
                  <Link to="/login">Login</Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Project Header */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div>
              <h1 className="text-3xl font-bold mb-2">{project.title}</h1>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="secondary">{project.category}</Badge>
                {project.status && (
                  <Badge variant={
                    project.status === "approved" ? "default" :
                      project.status === "pending_approval" ? "secondary" :
                        project.status === "funded" ? "default" :
                          project.status === "completed" ? "default" :
                            "outline"
                  }>
                    {project.status === "approved" && t("status.approved")}
                    {project.status === "pending_approval" && t("status.pending_approval")}
                    {project.status === "funded" && t("public.projects.status.funded")}
                    {project.status === "completed" && t("dashboard.projectholder.status.completed")}
                    {project.status === "draft" && t("dashboard.projectholder.status.draft")}
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex gap-2 flex-wrap">
              {user?.id === project.ownerId && userType === "project-holder" && (
                <Button variant="default" size="lg" asChild>
                  <Link to={`/projects/${projectId}/edit`}>
                    <Eye className="w-4 h-4 mr-2" />
                    {t("common.edit")} {t("common.project")}
                  </Link>
                </Button>
              )}
              {(userType === "admin" || user?.role === "admin") && (
                <Button variant="outline" size="lg" asChild>
                  <Link to={`/projects/${projectId}/edit`}>
                    <Eye className="w-4 h-4 mr-2" />
                    {t("common.edit")} {t("common.project")}
                  </Link>
                </Button>
              )}
              {userType === "investor" && project.status === "approved" && (
                <Button className="bg-hero-gradient hover:opacity-90 text-primary-foreground" onClick={handleInvest}>
                  <DollarSign className="w-4 h-4 mr-2" />
                  {t("public.projects.investNow")}
                </Button>
              )}

              {/* Show both Buy and Invest options for non-logged-in users */}
              {!user && (
                <>
                  <Button className="bg-hero-gradient hover:opacity-90 text-primary-foreground" asChild>
                    <Link to="/project-sales">
                      <DollarSign className="w-4 h-4 mr-2" />
                      {t("projectSale.buyProject") || "Buy Project"}
                    </Link>
                  </Button>
                  <Button variant="outline" onClick={() => {
                    toast.error("Please log in to invest in this project");
                    navigate("/login");
                  }}>
                    <DollarSign className="w-4 h-4 mr-2" />
                    {t("public.projects.investNow") || "Invest Now"}
                  </Button>
                </>
              )}
            </div>
          </div>

          <p className="text-muted-foreground text-lg">{project.description}</p>
        </div>

        {/* Project Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center mx-auto mb-2">
                <Target className="w-6 h-6 text-primary" />
              </div>
              <p className="text-2xl font-bold"><CurrencyDisplay amount={project.fundingGoal} /></p>
              <p className="text-sm text-muted-foreground">{t("common.goal")}</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center mx-auto mb-2">
                <DollarSign className="w-6 h-6 text-primary" />
              </div>
              <p className="text-2xl font-bold"><CurrencyDisplay amount={project.currentFunding} /></p>
              <p className="text-sm text-muted-foreground">{t("common.raised")}</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center mx-auto mb-2">
                <TrendingUp className="w-6 h-6 text-primary" />
              </div>
              <p className="text-2xl font-bold">{project.expectedROI}%</p>
              <p className="text-sm text-muted-foreground">{t("common.expectedReturn")}</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center mx-auto mb-2">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <p className="text-2xl font-bold">{fundingPercentage}%</p>
              <p className="text-sm text-muted-foreground">{t("common.completed")}</p>
            </CardContent>
          </Card>
        </div>

        {/* Funding Progress */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>{t("public.projects.fundingProgress")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between text-sm">
                <span>{t("public.projects.goal")}: <CurrencyDisplay amount={project.fundingGoal} /></span>
                <span>{t("public.projects.raised")}: <CurrencyDisplay amount={project.currentFunding} /></span>
              </div>
              <Progress value={fundingPercentage} className="h-3" />
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>{fundingPercentage}% {t("common.completed")}</span>
                <span>{t("public.projects.minInvestment")}: <CurrencyDisplay amount={project.minInvestment} /></span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images and Documents */}
          <div className="lg:col-span-1 space-y-6">
            {/* Project Images */}
            {project.images && project.images.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="w-5 h-5" />
                    {t("public.projects.projectImages")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2">
                    {project.images.map((image, index) => (
                      <div key={index} className="aspect-square bg-muted rounded-lg overflow-hidden">
                        <img
                          src={image}
                          alt={`Project image ${index + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = '/placeholder-image.jpg'; // fallback image
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Project Documents */}
            {project.documents && project.documents.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    {t("public.projects.projectDocuments")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {project.documents.map((document, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-primary" />
                          <span className="text-sm font-medium truncate max-w-[160px]">Document {index + 1}</span>
                        </div>
                        <Button variant="outline" size="sm" asChild>
                          <a href={document} target="_blank" rel="noopener noreferrer">
                            <Download className="w-4 h-4 mr-1" />
                            {t("common.download")}
                          </a>
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}


          </div>

          {/* Right Column - Project Details */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t("public.projects.projectDetails")}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium mb-2">{t("public.projects.basicInformation")}</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t("common.category")}:</span>
                        <span>{project.category}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t("common.status")}:</span>
                        <span>
                          {project.status === "approved" && t("status.approved")}
                          {project.status === "pending_approval" && t("status.pending_approval")}
                          {project.status === "funded" && t("public.projects.status.funded")}
                          {project.status === "completed" && t("dashboard.projectholder.status.completed")}
                          {project.status === "draft" && t("dashboard.projectholder.status.draft")}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t("public.projects.minInvestment")}:</span>
                        <span><CurrencyDisplay amount={project.minInvestment} /></span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">{t("public.projects.financialInformation")}</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t("public.projects.financialGoal")}:</span>
                        <span><CurrencyDisplay amount={project.fundingGoal} /></span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t("public.projects.raisedSoFar")}:</span>
                        <span><CurrencyDisplay amount={project.currentFunding} /></span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t("common.expectedReturn")}:</span>
                        <span>{project.expectedROI}%</span>
                      </div>
                    </div>
                  </div>
                </div>

                {userType === "investor" && project.status === "approved" && project.currentFunding < project.fundingGoal && (
                  <div className="mt-6">
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <h4 className="font-medium mb-2">{t("public.projects.investInThisProject")}</h4>
                      <p className="text-sm text-muted-foreground mb-4">
                        {t("public.projects.minInvestmentIs")}: <CurrencyDisplay amount={project.minInvestment} />
                      </p>
                      <Button className="w-full bg-hero-gradient hover:opacity-90 text-primary-foreground" onClick={handleInvest}>
                        <DollarSign className="w-4 h-4 mr-2" />
                        {t("public.projects.investNow")}
                      </Button>
                    </div>
                  </div>
                )}

                {userType === "investor" && project.status === "approved" && project.currentFunding >= project.fundingGoal && (
                  <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                    <h4 className="font-medium mb-2">{t("public.projects.projectFullyFunded")}</h4>
                    <p className="text-sm text-muted-foreground mb-4">
                      {t("public.projects.projectReachedFundingGoal")}: <CurrencyDisplay amount={project.fundingGoal} />
                    </p>
                    <Button className="w-full" variant="outline" disabled>
                      <DollarSign className="w-4 h-4 mr-2" />
                      {t("public.projects.cannotInvest")}
                    </Button>
                  </div>
                )}

                {showInvestmentModal && project && (
                  <InvestmentModal
                    projectId={project._id}
                    minInvestment={project.minInvestment}
                    maxInvestment={Math.max(0, project.fundingGoal - project.currentFunding)}
                    projectTitle={project.title}
                    projectFundingGoal={project.fundingGoal}
                    userInvestmentCapacity={user?.investmentCapacity}
                    isOpen={showInvestmentModal}
                    onClose={() => setShowInvestmentModal(false)}
                  />
                )}

              </CardContent>
            </Card>

            {/* Project Description */}
            <Card>
              <CardHeader>
                <CardTitle>{t("public.projects.projectDescription")}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground whitespace-pre-line">
                  {project.description}
                </p>
              </CardContent>
            </Card>

            {/* Category-specific sections */}
            {project.category === 'Innovation' && (
              <div className="space-y-6">
                {/* Innovation Overview */}
                <Card>
                  <CardHeader>
                    <CardTitle>Innovation Overview</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground whitespace-pre-line mb-4">
                      {project.solutionDescription || "No solution description provided"}
                    </p>
                    {project.problemStatement && (
                      <div className="mb-4">
                        <h4 className="font-medium mb-2">Problem Statement</h4>
                        <p className="text-muted-foreground whitespace-pre-line">{project.problemStatement}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Development Stage */}
                {project.developmentStage && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Development Stage</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">{project.developmentStage}</Badge>
                        <p className="text-muted-foreground ml-2">
                          {project.developmentStage === 'Idea' && 'Early concept stage'}
                          {project.developmentStage === 'Prototype' && 'Prototype built and tested'}
                          {project.developmentStage === 'MVP' && 'Minimum viable product tested'}
                          {project.developmentStage === 'Patent Filed' && 'Patent application submitted'}
                          {project.developmentStage === 'Market-ready' && 'Ready for market'}
                          {project.developmentStage === 'Generating Revenue' && 'Already generating revenue'}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Technology Details */}
                {project.technologyDetails && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Technology & Design Details</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground whitespace-pre-line">{project.technologyDetails}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Intellectual Property */}
                {project.ipStatus && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Intellectual Property & Ownership</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="secondary">{project.ipStatus}</Badge>
                      </div>
                      <p className="text-muted-foreground">
                        This project has {project.ipStatus.toLowerCase()} protection.
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Market Potential */}
                {project.marketPotential && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Market Potential</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground whitespace-pre-line">{project.marketPotential}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Monetization Options */}
                {project.monetizationOptions && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Monetization Options</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground whitespace-pre-line">{project.monetizationOptions}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Assets Included */}
                {project.assetsIncluded && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Assets Included in the Sale</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground whitespace-pre-line">{project.assetsIncluded}</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProjectDetail;